import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePagination } from '../hooks/usePagination';
import { useAuth } from '../context/AuthContext';
import Pagination from './PaginationNew';
import { RewardLogService } from '../services/rewardLogService';
import type { RewardLog } from '../types/rewardLog';

// Reward calculation constants and helper functions
const rpValueInInr = 0.25;

const getMonthlyAmount = (amount: number, frequency?: string) => {
  switch (frequency) {
    case 'quarterly':
      return amount / 3;
    case 'half_yearly':
      return amount / 6;
    case 'annually':
      return amount / 12;
    default:
      return amount;
  }
};

const calculateRewardPoints = (amount: number, frequency: string | undefined, effectiveRatePerInr: number, monthlyLimit?: number) => {
  const monthlyAmount = getMonthlyAmount(amount, frequency);
  let rewardPoints = monthlyAmount * effectiveRatePerInr;
  if (typeof monthlyLimit === 'number' && monthlyLimit > 0 && rewardPoints > monthlyLimit) {
    rewardPoints = monthlyLimit;
  }
  return Math.round(rewardPoints);
};

const calculateRewardPercentage = (amount: number, frequency: string | undefined, rewardPoints: number) => {
  const monthlyAmount = getMonthlyAmount(amount, frequency);
  if (!monthlyAmount || monthlyAmount <= 0) return 0;
  const rewardValue = rewardPoints * rpValueInInr;
  return Math.round((rewardValue / monthlyAmount) * 100 * 10) / 10;
};

const accelerationRatio = (acceleratedValue?: number, baseValue?: number) => {
  if (typeof acceleratedValue === 'number' && typeof baseValue === 'number' && baseValue > 0) {
    return Math.round(acceleratedValue / baseValue);
  }
  return null;
};

const RewardLogsTable: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const { currentPage, itemsPerPage, setPage, setLimit } = usePagination({
    defaultPage: 1,
    defaultLimit: 50,
  });

  const [logs, setLogs] = useState<RewardLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [modalRewards, setModalRewards] = useState<RewardLog['rewards'] | null>(null);
  const [modalLog, setModalLog] = useState<RewardLog | null>(null);

  // Filter states
  const [filterUserEmail, setFilterUserEmail] = useState<string>('');
  const [filterUserName, setFilterUserName] = useState<string>('');
  const [filterResultIds, setFilterResultIds] = useState<'all' | '0' | '1+'>('all');

  useEffect(() => {
    const title = currentPage > 1 ? `Reward Logs - Page ${currentPage} of ${totalPages}` : 'Reward Logs';
    document.title = title;
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!isAdmin) return;
    let isMounted = true;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const params: any = {
          page: currentPage,
          limit: itemsPerPage,
        };

        if (filterUserEmail) params.userEmail = filterUserEmail;
        if (filterUserName) params.userName = filterUserName;
        if (filterResultIds !== 'all') params.resultIds = filterResultIds;

        const response = await RewardLogService.getRewardLogs(params);

        if (isMounted) {
          setLogs(response.data);
          setTotalPages(response.pagination?.pages ?? 1);
          setTotalItems(response.pagination?.total ?? 0);
        }
      } catch (err: any) {
        if (isMounted) {
          console.error('Failed to load reward logs:', err);
          setError(err?.message || 'Failed to load reward logs');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAdmin, currentPage, itemsPerPage, filterUserEmail, filterUserName, filterResultIds]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900">Access Denied</h2>
          <p className="mt-2 text-gray-600">You must be an admin to view reward logs.</p>
        </div>
      </div>
    );
  }

  const handleClearFilters = () => {
    setFilterUserEmail('');
    setFilterUserName('');
    setFilterResultIds('all');
    setPage(1);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getUserName = (log: RewardLog) => log.user?.name || log.userName;
  const getUserEmail = (log: RewardLog) => log.user?.email || log.userEmail;
  const getUserRegisteredAt = (log: RewardLog) => log.user?.createdAt || log.userRegisteredAt;

  const openRewardsModal = (rewards: RewardLog['rewards'], log: RewardLog) => {
    if (!rewards || rewards.length === 0) return;
    setModalRewards(rewards);
    setModalLog(log);
  };

  const closeModal = () => {
    setModalRewards(null);
    setModalLog(null);
  };

  const openRewardOptionDetail = (reward: NonNullable<RewardLog['rewards']>[number], log: RewardLog) => {
    navigate('/rewardsv1/option-detail', {
      state: {
        rewardOption: reward as any,
        currentAmount: log.questionContext?.amount,
        currentFrequency: log.questionContext?.frequency,
        from: location.pathname + location.search,
      },
    });
  };

  useEffect(() => {
    if (!modalRewards) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modalRewards]);

  const firstNonEmptyString = (...values: Array<unknown>) => {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) return value;
    }
    return undefined;
  };

  const renderLabelValue = (label: string, value?: string) => {
    if (!value) return null;
    return (
      <div>
        <span className="font-semibold">{label}:</span> {value}
      </div>
    );
  };

  const renderRewardDetails = (reward: NonNullable<RewardLog['rewards']>[number], log: RewardLog) => {
    const r: any = reward;

    const rewardName = firstNonEmptyString(r.name, r.shortName);
    const bank = firstNonEmptyString(r.bankName, r.bankSlug, r.bank_name, r.bankId?.bankName, r.bankId?.bankSlug, r.bankId?.name);
    const card = firstNonEmptyString(r.cardName, r.cardSlug, r.card_name, r.cardId?.name, r.cardId?.cardName, r.cardId?.cardSlug);
    const category = firstNonEmptyString(r.categoryPrimary, r.categoryprimary);
    const subcategory = firstNonEmptyString(r.subcategory);
    const categorySecondary = Array.isArray(r.categorysecondary) && r.categorysecondary.length ? r.categorysecondary.join(', ') : undefined;
    const status = firstNonEmptyString(r.status);

    // Calculate reward details if we have the necessary data
    const amount = log.questionContext?.amount;
    const frequency = log.questionContext?.frequency;
    const effectiveRate = r.rewards?.dynamic?.effective_rate_per_inr ?? 0;
    const monthlyLimit = r.rewards?.limit;
    const rewardDescription = r.rewards?.description;

    const canCalc = typeof amount === 'number' && Number.isFinite(amount) && amount > 0 && effectiveRate > 0;
    const rewardPoints = canCalc ? calculateRewardPoints(amount, frequency, effectiveRate, monthlyLimit) : null;
    const pct = canCalc && rewardPoints !== null ? calculateRewardPercentage(amount, frequency, rewardPoints) : null;
    const accel = accelerationRatio(r.rewards?.dynamic?.accelerated_value, r.rewards?.dynamic?.base_value);

    const getFrequencyLabel = (freq?: string) => {
      switch (freq) {
        case 'quarterly': return 'quarterly';
        case 'half_yearly': return 'half-yearly';
        case 'annually': return 'annual';
        default: return 'monthly';
      }
    };

    return (
      <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
        <div className="space-y-1">
          {renderLabelValue('Name', rewardName)}
          {renderLabelValue('Bank', bank)}
          {renderLabelValue('Card', card)}
          {renderLabelValue('Category', category)}
          {renderLabelValue('Category 2nd', categorySecondary)}
          {renderLabelValue('Subcategory', subcategory)}
          {renderLabelValue('Status', status)}
        </div>

        {/* Reward Calculation Information */}
        {canCalc && rewardPoints !== null && (
          <div className="mt-3 pt-3 border-t border-gray-300 space-y-2">
            <div className="font-semibold text-sm text-gray-900">Reward Calculation</div>
            
            <div className="space-y-1 bg-white p-2 rounded border border-gray-200">
              <div>
                <span className="font-semibold">Search Amount:</span> ₹{amount.toLocaleString('en-IN')} ({getFrequencyLabel(frequency)})
              </div>
              <div>
                <span className="font-semibold">Monthly Amount:</span> ₹{getMonthlyAmount(amount, frequency).toLocaleString('en-IN')}
              </div>
              {rewardDescription && (
                <div>
                  <span className="font-semibold">Reward Rate:</span> {rewardDescription}
                </div>
              )}
              {typeof monthlyLimit === 'number' && monthlyLimit > 0 && (
                <div>
                  <span className="font-semibold">Monthly Cap:</span> {monthlyLimit.toLocaleString('en-IN')} RPs
                </div>
              )}
              <div className="pt-2 border-t border-gray-200">
                <span className="font-semibold">Reward Points Earned:</span>{' '}
                <span className="text-blue-700 font-bold text-sm">{rewardPoints.toLocaleString('en-IN')} RPs</span>
              </div>
              {pct !== null && (
                <div>
                  <span className="font-semibold">Reward Value:</span>{' '}
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {pct}% rewards
                  </span>
                </div>
              )}
              {accel !== null && (
                <div>
                  <span className="font-semibold">Acceleration:</span>{' '}
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {accel}X Returns
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {r.rewards && (
          <details className="mt-2">
            <summary className="cursor-pointer select-none text-xs font-semibold text-blue-700 hover:text-blue-900">
              Raw reward data
            </summary>
            <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-white p-2 text-[11px] text-gray-800 border border-gray-200">
              {typeof r.rewards === 'string' ? r.rewards : JSON.stringify(r.rewards, null, 2)}
            </pre>
          </details>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-[98%] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Reward Search Logs</h1>
                <p className="mt-1 text-sm text-gray-500">
                  View and analyze reward search activity logs
                </p>
              </div>
            </div>

            {/* Filters */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="User email..."
                value={filterUserEmail}
                onChange={(e) => setFilterUserEmail(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="User name..."
                value={filterUserName}
                onChange={(e) => setFilterUserName(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={filterResultIds}
                onChange={(e) => setFilterResultIds(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All results</option>
                <option value="0">0</option>
                <option value="1+">1+</option>
              </select>
            </div>

            {(filterUserEmail || filterUserName || filterResultIds !== 'all') && (
              <div className="mt-3">
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="px-6 py-4 bg-red-50 border-b border-red-200">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="px-6 py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-2 text-sm text-gray-500">Loading logs...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="text-gray-500">No logs found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Timestamp
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cards
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Question Context
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Search Query
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Results
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {logs.map((log) => (
                      <tr key={log._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="space-y-0.5">
                            <div className="font-medium">{getUserName(log) || '—'}</div>
                            <div className="text-xs text-gray-600">{getUserEmail(log) || '—'}</div>
                            <div className="text-xs text-gray-500">
                              Registered: {getUserRegisteredAt(log) ? formatTimestamp(getUserRegisteredAt(log) as string) : '—'}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {(() => {
                            const count = log.userCardCount ?? log.userCardsSnapshot?.length;
                            const names = (log.userCardsSnapshot || [])
                              .map((c) => c.cardName)
                              .filter((v): v is string => typeof v === 'string' && !!v.trim());
                            const shown = names.slice(0, 2);
                            const remaining = names.length - shown.length;

                            return (
                              <div className="space-y-0.5">
                                {/* <div className="text-xs text-gray-600">Count: {typeof count === 'number' ? count : '—'}</div> */}
                                {shown.length > 0 ? (
                                  <div className="text-sm">
                                    {shown.join(', ')}{remaining > 0 ? ` +${remaining}` : ''}
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-500">—</div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {log.questionContext ? (
                            <div className="space-y-0.5">
                              <div className="font-medium">{log.questionContext.name || log.questionContext.questionId || '—'}</div>
                              <div className="text-xs text-gray-600">
                                {(log.questionContext.category || '—')}
                                {log.questionContext.frequency ? ` • ${log.questionContext.frequency}` : ''}
                              </div>
                              <div className="text-xs text-gray-500">
                                Amount: {typeof log.questionContext.amount === 'number' ? log.questionContext.amount.toLocaleString() : '—'}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                          {log.searchQuery || <span className="text-gray-400 italic">N/A</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {log.resultIds && log.resultIds.length > 0 ? (
                            <div>
                              <span className="font-semibold">{log.resultIds.length}</span>
                              {log.rewards && log.rewards.length > 0 && (
                                <div className="mt-1 text-xs text-gray-500">
                                  <button
                                    type="button"
                                    className="text-blue-600 hover:text-blue-800 font-medium"
                                    onClick={() => openRewardsModal(log.rewards || [], log)}
                                  >
                                    View rewards
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Showing <span className="font-medium">{logs.length}</span> of{' '}
                    <span className="font-medium">{totalItems}</span> logs
                  </div>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    itemsPerPage={itemsPerPage}
                    totalItems={totalItems}
                    onPageChange={setPage}
                    onLimitChange={setLimit}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {modalRewards && modalRewards.length > 0 && modalLog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
          onClick={closeModal}
          role="presentation"
        >
          <div className="w-full max-w-4xl rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Reward Details</h2>
                <p className="text-sm text-gray-500">Showing {modalRewards.length} rewards</p>
                {modalLog.questionContext && (
                  <div className="mt-2 text-sm text-gray-600">
                    Search: {modalLog.questionContext.name || 'N/A'} • 
                    ₹{modalLog.questionContext.amount?.toLocaleString('en-IN') || '—'} ({modalLog.questionContext.frequency || 'monthly'})
                  </div>
                )}
              </div>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                onClick={closeModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-6 py-4 space-y-4">
              {modalRewards.map((reward) => (
                <div key={reward._id} className="border border-gray-200 rounded-md p-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="font-semibold text-sm text-gray-900">
                      {(reward as any).name || (reward as any).shortName || (reward as any).card_name || 'Reward'}
                    </div>
                    <button
                      type="button"
                      className="inline-flex items-center rounded-md border border-blue-600 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
                      onClick={() => {
                        openRewardOptionDetail(reward, modalLog);
                        closeModal();
                      }}
                    >
                      View Details
                    </button>
                  </div>
                  {renderRewardDetails(reward, modalLog)}
                </div>
              ))}
            </div>
            <div className="flex justify-end border-t px-6 py-3">
              <button
                type="button"
                className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={closeModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardLogsTable;
